/**
 * Handler registered by the ScreenLogCard component to show the card.
 */
export type ScreenLogHandler = (text: string) => void;

/**
 * Service to print logs on screen inside a card.
 *
 * Used together with components/ScreenLogCard, which must be mounted at the
 * top level of the app and register itself via setHandler.
 *
 * Usage: ScreenLogService.open('text to show');
 */
class ScreenLogService {

    // Handler that shows the card, registered by the ScreenLogCard component.
    static handler: ScreenLogHandler | undefined;

    // Pending text if open() is called before the component is mounted.
    static pendingText: string | null = null;

    /**
     * Connects the ScreenLogCard component with the service.
     */
    static setHandler(handler: ScreenLogHandler): void {
        this.handler = handler;
        // If the card was requested before the component was mounted
        // (e.g. during initialization), show it now.
        if (this.pendingText !== null) {
            handler(this.pendingText);
            this.pendingText = null;
        }
    }

    /**
     * Opens the card with the given text.
     */
    static open(text: string): void {
        if (this.handler) {
            this.handler(text);
        } else {
            // The component is not mounted yet; store it to show it on mount.
            this.pendingText = text;
        }
    }
}

export default ScreenLogService;
